import { useCallback, useEffect, useRef } from "react";
import { loadTurnstileScript } from "./turnstile-script";

/**
 * Renders a Turnstile widget when `siteKey` is non-empty and `enabled` is true.
 * `siteKey === null` means config is still loading from `/api/auth/config`.
 */
export function useTurnstile(siteKey: string | null, enabled: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (siteKey === null || !siteKey || !enabled) return;
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    (async () => {
      try {
        await loadTurnstileScript();
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: "dark",
          callback: (t: string) => {
            tokenRef.current = t;
          },
          "expired-callback": () => {
            tokenRef.current = null;
          },
          "error-callback": () => {
            tokenRef.current = null;
          },
        });
      } catch {
        /* script load failure */
      }
    })();

    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      widgetIdRef.current = null;
      tokenRef.current = null;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          /* ignore */
        }
      }
      el.replaceChildren();
    };
  }, [siteKey, enabled]);

  const reset = useCallback(() => {
    tokenRef.current = null;
    const id = widgetIdRef.current;
    if (id && window.turnstile) {
      try {
        window.turnstile.reset(id);
      } catch {
        /* ignore */
      }
    }
  }, []);

  return {
    containerRef,
    getToken: () => tokenRef.current,
    reset,
    loadingConfig: siteKey === null,
    required: Boolean(siteKey),
  };
}
